import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { TASK_KEYS } from '@/features/tasks/public';
import { checklistItemsService } from '../api/checklists.service';
import { CHECKLIST_KEYS } from './queryKeys';
import type {
  AddChecklistItemPayload,
  Checklist,
  MoveChecklistItemPayload,
  UpdateChecklistItemPayload,
} from '../types';

export function useAddChecklistItem(taskId: string) {
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId);

  return useMutation({
    mutationFn: ({ checklistId, data }: { checklistId: string; data: AddChecklistItemPayload }) =>
      checklistItemsService.add(checklistId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CHECKLIST_KEYS.byTask(orgId ?? '', taskId) });
    },
  });
}

/** Updates an item (title or checked state). Toggling is applied optimistically. */
export function useUpdateChecklistItem(taskId: string) {
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId);
  const key = CHECKLIST_KEYS.byTask(orgId ?? '', taskId);

  return useMutation({
    mutationFn: ({ itemId, data }: { itemId: string; data: UpdateChecklistItemPayload }) =>
      checklistItemsService.update(itemId, data),
    onMutate: async ({ itemId, data }) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<Checklist[]>(key);
      if (previous) {
        queryClient.setQueryData<Checklist[]>(
          key,
          previous.map((checklist) => {
            const items = checklist.items.map((item) =>
              item.id === itemId
                ? {
                    ...item,
                    ...(data.title !== undefined ? { title: data.title } : {}),
                    ...(data.isChecked !== undefined ? { isChecked: data.isChecked } : {}),
                  }
                : item,
            );
            const checkedItems = items.filter((i) => i.isChecked).length;
            return {
              ...checklist,
              items,
              checkedItems,
              progress: items.length === 0 ? 0 : (checkedItems / items.length) * 100,
            };
          }),
        );
      }
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: key });
    },
  });
}

export function useDeleteChecklistItem(taskId: string) {
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId);

  return useMutation({
    mutationFn: (itemId: string) => checklistItemsService.delete(itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CHECKLIST_KEYS.byTask(orgId ?? '', taskId) });
    },
  });
}

export function useMoveChecklistItem(taskId: string) {
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId);

  return useMutation({
    mutationFn: ({ itemId, data }: { itemId: string; data: MoveChecklistItemPayload }) =>
      checklistItemsService.move(itemId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CHECKLIST_KEYS.byTask(orgId ?? '', taskId) });
    },
  });
}

/** Converts an item into a new sibling task. The item is removed from its checklist. */
export function useConvertItemToTask(taskId: string) {
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId);

  return useMutation({
    mutationFn: (itemId: string) => checklistItemsService.convertToTask(itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CHECKLIST_KEYS.byTask(orgId ?? '', taskId) });
      queryClient.invalidateQueries({ queryKey: TASK_KEYS.byOrg(orgId ?? '') });
    },
  });
}

/** Converts an item into a subtask of the current task (main tasks only). */
export function useConvertItemToSubtask(taskId: string) {
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId);

  return useMutation({
    mutationFn: (itemId: string) => checklistItemsService.convertToSubtask(itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CHECKLIST_KEYS.byTask(orgId ?? '', taskId) });
      queryClient.invalidateQueries({ queryKey: TASK_KEYS.subtasks(orgId ?? '', taskId) });
      queryClient.invalidateQueries({ queryKey: TASK_KEYS.detail(orgId ?? '', taskId) });
    },
  });
}
